import { useMemo, useState } from 'react';
import {
  Search,
  Download,
  ExternalLink,
  FileText,
  DollarSign,
  CreditCard,
  Gift,
} from 'lucide-react';
import { useDebounce, useUrlTable } from '@/hooks';
import { useOrganizations } from '@/hooks/api/useOrganizations';
import {
  useBillingOverview,
  useUpdateBillingFromOverview,
  useHubspotStatus,
} from '@/hooks/api/useBilling';
import { billingApi, BillingOverviewFilters, BillingOverviewStatusFilter } from '@/api/billing';
import { BillingStatus } from '@/api/projects';
import { useToast } from '@/components/ui/use-toast';
import { Switch } from '@/components/ui/switch';
import { getErrorMessage } from '@/utils/errorUtils';
import Routes from '@/router/routes';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { SortableTableHead, SortOrder } from '@/components/ui/sortable-table-head';
import { TableSkeleton } from '@/components/ui/table-skeleton';
import { Badge } from '@/components/ui/badge';
import { getBillingBadgeVariant, getBillingStatusLabel } from '@/utils/billingDisplay';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { formatDateTime } from '@/lib/date';

interface TableState {
  page: number;
  limit: number;
  search: string;
  organization_id: string;
  billingStatus: string;
  subscribed: string;
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
}

function formatCurrency(amount: number | null, currency: string | null): string | null {
  if (amount === undefined || amount === null) return null;
  try {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'EUR',
    }).format(amount);
  } catch {
    return `${amount} ${currency || ''}`.trim();
  }
}

function HubspotDealCell({
  dealLink,
  hubspotDealId,
}: {
  dealLink: string | null;
  hubspotDealId: string | null;
}) {
  if (dealLink) {
    return (
      <a
        href={dealLink}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-1 text-blue-600 hover:underline"
        title={hubspotDealId ?? undefined}
      >
        <DollarSign className="h-3.5 w-3.5" />
        Deal
        <ExternalLink className="h-3.5 w-3.5" />
      </a>
    );
  }
  if (hubspotDealId) {
    return <span className="text-sm text-muted-foreground">{hubspotDealId}</span>;
  }
  return <span className="text-sm text-muted-foreground">—</span>;
}

/** Local blob-download helper — mirrors the pattern used for 2FA backup codes. */
function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export default function SuperAdminBilling() {
  const { state, setPage, setSearch, setFilter, setSort } = useUrlTable<TableState>({
    page: 1,
    limit: 25,
    search: '',
    organization_id: '',
    billingStatus: '',
    subscribed: '',
    sortBy: '',
    sortOrder: 'desc',
  });

  const [exporting, setExporting] = useState<'csv' | 'xlsx' | null>(null);
  const { toast } = useToast();

  const debouncedSearch = useDebounce(state.search, 300);

  const filters: BillingOverviewFilters = useMemo(
    () => ({
      page: state.page,
      limit: state.limit,
      search: debouncedSearch || undefined,
      organization_id: state.organization_id || undefined,
      billingStatus: (state.billingStatus || undefined) as BillingOverviewStatusFilter | undefined,
      subscribed: state.subscribed === 'true' || undefined,
      sortBy: (state.sortBy || undefined) as BillingOverviewFilters['sortBy'],
      sortOrder: state.sortOrder,
    }),
    [
      state.page,
      state.limit,
      debouncedSearch,
      state.organization_id,
      state.billingStatus,
      state.subscribed,
      state.sortBy,
      state.sortOrder,
    ],
  );

  const { data, isLoading, isError } = useBillingOverview(filters);
  const { data: hubspotStatus } = useHubspotStatus();
  const updateBilling = useUpdateBillingFromOverview();

  // Generous limit, sorted by name — feeds the organization filter select.
  const organizationsFilters = useMemo(
    () => ({ page: 1, limit: 500, sortBy: 'name' as const, sortOrder: 'asc' as const }),
    [],
  );
  const { data: organizationsData } = useOrganizations(organizationsFilters);
  const organizations = organizationsData?.data ?? [];

  const handleSort = (sortBy: string, sortOrder: SortOrder) => {
    setSort(sortBy, sortOrder);
  };

  const handleBillingStatusChange = (projectId: string, status: BillingStatus | null) => {
    updateBilling.mutate({ id: projectId, payload: { billingStatus: status } });
  };

  const handleExport = async (fileType: 'csv' | 'xlsx') => {
    setExporting(fileType);
    try {
      // The export endpoint streams every matching row itself, ignoring
      // pagination — only the filter/sort params are relevant here.
      // Everything except pagination — destructured from the live filters so
      // a future filter key cannot be silently omitted from the export again
      // (PR #1691 review: `subscribed` was missing from a hand-built list).
      const { page: _page, limit: _limit, ...exportFilters } = filters;
      const blob = await billingApi.exportOverview(exportFilters, fileType);
      const date = new Date().toISOString().slice(0, 10);
      downloadBlob(blob, `billing-overview-${date}.${fileType}`);
    } catch (error) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to export billing overview'),
        variant: 'destructive',
      });
    } finally {
      setExporting(null);
    }
  };

  const totalPages = data ? data.totalPages : 0;
  const isEmpty = !isLoading && !isError && (!data || data.data.length === 0);
  const hasRows = !isLoading && !isError && !isEmpty;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-foreground">Billing</h1>
          <p className="text-muted-foreground mt-1">
            Interim billing overview across all organizations and projects
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('csv')}
            disabled={exporting !== null}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === 'csv' ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => handleExport('xlsx')}
            disabled={exporting !== null}
          >
            <Download className="h-4 w-4 mr-2" />
            {exporting === 'xlsx' ? 'Exporting...' : 'Export XLSX'}
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-card rounded-lg shadow-sm border border-border p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder="Search by project title..."
              value={state.search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>

          <Select
            value={state.organization_id || 'all'}
            onValueChange={(value) =>
              setFilter('organization_id', value === 'all' ? undefined : value)
            }
          >
            <SelectTrigger className="w-full sm:w-[220px]">
              <SelectValue placeholder="Organization" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All organizations</SelectItem>
              {organizations.map((org) => (
                <SelectItem key={org._id} value={org._id}>
                  {org.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select
            value={state.billingStatus || 'all'}
            onValueChange={(value) =>
              setFilter('billingStatus', value === 'all' ? undefined : value)
            }
          >
            <SelectTrigger className="w-full sm:w-[180px]">
              <SelectValue placeholder="Billing status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              <SelectItem value="NEW">New</SelectItem>
              <SelectItem value="INVOICED">Invoiced</SelectItem>
              <SelectItem value="PAID">Paid</SelectItem>
              <SelectItem value="FREE">Free</SelectItem>
            </SelectContent>
          </Select>

          <label
            htmlFor="paid-orgs-only"
            className="flex items-center gap-2 text-sm text-muted-foreground whitespace-nowrap cursor-pointer"
          >
            <Switch
              id="paid-orgs-only"
              checked={state.subscribed === 'true'}
              onCheckedChange={(checked) => setFilter('subscribed', checked ? 'true' : undefined)}
            />
            Paid orgs only
          </label>
        </div>
      </div>

      {/* Table */}
      <div className="bg-card rounded-lg shadow-sm border border-border">
        {isLoading && (
          <TableSkeleton
            columns={8}
            rows={10}
            headers={[
              'Organization',
              'Workspace',
              'Project',
              'Completed',
              'Billing status',
              'HubSpot deal',
              'Amount',
              'Updated',
            ]}
          />
        )}

        {isError && (
          <div className="p-8 text-center">
            <p className="text-red-600">Failed to load billing overview. Please try again.</p>
          </div>
        )}

        {isEmpty && (
          <div className="p-8 text-center">
            <p className="text-muted-foreground">No projects found.</p>
          </div>
        )}

        {hasRows && data && (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Organization</TableHead>
                  <TableHead>Workspace</TableHead>
                  <SortableTableHead
                    sortKey="title"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Project
                  </SortableTableHead>
                  <TableHead>Completed</TableHead>
                  <SortableTableHead
                    sortKey="billingStatus"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Billing status
                  </SortableTableHead>
                  <TableHead>HubSpot deal</TableHead>
                  <TableHead>Amount</TableHead>
                  <SortableTableHead
                    sortKey="createdAt"
                    currentSortBy={state.sortBy}
                    currentSortOrder={state.sortOrder as SortOrder}
                    onSort={handleSort}
                  >
                    Updated
                  </SortableTableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.data.map((row) => {
                  const amount = formatCurrency(row.billingAmount, row.billingCurrency);
                  const dealLink =
                    hubspotStatus?.portalId && row.hubspotDealId
                      ? `https://app.hubspot.com/contacts/${hubspotStatus.portalId}/deal/${row.hubspotDealId}`
                      : null;

                  return (
                    <TableRow key={row._id}>
                      <TableCell className="text-muted-foreground">
                        {row.organizationName || 'N/A'}
                      </TableCell>
                      <TableCell className="text-muted-foreground">
                        {row.workspaceName || 'N/A'}
                      </TableCell>
                      <TableCell className="font-medium">
                        <a
                          href={`${Routes.super_admin_dashboard_projects.path}/${row._id}`}
                          className="text-blue-600 hover:underline"
                        >
                          {row.title}
                        </a>
                      </TableCell>
                      <TableCell>{row.completedCount}</TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button type="button" className="cursor-pointer">
                              <Badge variant={getBillingBadgeVariant(row.billingStatus)}>
                                {getBillingStatusLabel(row.billingStatus)}
                              </Badge>
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="start">
                            <DropdownMenuItem
                              onClick={() => handleBillingStatusChange(row._id, null)}
                              disabled={!row.billingStatus}
                            >
                              New
                              {!row.billingStatus && ' ✓'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() =>
                                handleBillingStatusChange(row._id, BillingStatus.INVOICED)
                              }
                              disabled={row.billingStatus === BillingStatus.INVOICED}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Invoiced
                              {row.billingStatus === BillingStatus.INVOICED && ' ✓'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleBillingStatusChange(row._id, BillingStatus.PAID)}
                              disabled={row.billingStatus === BillingStatus.PAID}
                            >
                              <CreditCard className="h-4 w-4 mr-2" />
                              Paid
                              {row.billingStatus === BillingStatus.PAID && ' ✓'}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleBillingStatusChange(row._id, BillingStatus.FREE)}
                              disabled={row.billingStatus === BillingStatus.FREE}
                            >
                              <Gift className="h-4 w-4 mr-2" />
                              Free
                              {row.billingStatus === BillingStatus.FREE && ' ✓'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                      <TableCell>
                        <HubspotDealCell dealLink={dealLink} hubspotDealId={row.hubspotDealId} />
                      </TableCell>
                      <TableCell className="text-muted-foreground">{amount || '—'}</TableCell>
                      <TableCell className="text-muted-foreground whitespace-nowrap">
                        {row.billingStatusUpdatedAt
                          ? formatDateTime(row.billingStatusUpdatedAt)
                          : '—'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t border-border px-4 py-3">
              <div className="text-sm text-muted-foreground">
                {data.totalPages > 1 ? (
                  <>
                    Showing {(data.page - 1) * data.limit + 1} to{' '}
                    {Math.min(data.page * data.limit, data.total)} of {data.total} projects
                  </>
                ) : (
                  <>
                    Showing {data.total} {data.total === 1 ? 'project' : 'projects'}
                  </>
                )}
              </div>
              {totalPages > 1 && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page - 1)}
                    disabled={state.page === 1}
                  >
                    Previous
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage(state.page + 1)}
                    disabled={state.page >= totalPages}
                  >
                    Next
                  </Button>
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
